// BFX
import * as CubiqTS from "shared/cubiq-model-multi-asset";
// order-entry enums
import { OENDisplayState } from "../enums/oe-display-state.enum";
import { OENOperationMode } from "../enums/oe-operation-mode.enum";
import { OENEntryType } from "../enums/oe-entry-type.enum";
import { OEStrategyModel } from "./oe-strategy.model";
// interface control models
import {
  ICAutocompleteGenericModel,
  ICCommentsModel,
  ICMarkupModel,
} from "../../interface/controls/models/bfx-interface-controls.models";
import {
  ICFixedCcyModel,
  ICSuspendModel,
  ICTimeControlModel,
  ICTenorValueDateModel,
  ICFixingSourceModel,
} from "../../interface/controls/models/bfx-interface-controls.models";
import { ICGoodFromUntilModel } from "../../interface/controls/models/bfx-good-from-until.model";
// order entry models
import { OEPrivatizationLockingProfileModel } from "./oe-locking-profile.model";
import { OEUpdateEventModel } from "./oe-update-event.model";
import { ICLimitPriceModel } from "../../interface/controls/models/ic-limit-price.model";
import { ATDLParameterDataModel } from "../../data-atdl/model/atdl-parameter-data.model";
import { ATDLEnumModel } from "../../data-atdl/model/atdl-enum.model";
import { ICDAutocompleteModel } from "../../data-idc/controls/icd-autocomplete.model";
import { ICDAuxCheckboxModel } from "../../data-idc/controls/icd-aux-checkbox.model";
import { ICDSpinnerModel } from "../../data-idc/controls/icd-spinner.model";
import { ICDTextFieldModel } from "../../data-idc/controls/icd-text-field.model";
import { ICDClockModel } from "../../data-idc/controls/icd-clock.model";
import { ICDRadioInputModel } from "../../data-idc/controls/icd-radio-input.model";
import { ICDCheckboxModel } from "../../data-idc/controls/icd-checkbox.model";
import { OEPanelGammaViewModel } from "./oe-panel-gamma-view.model";
import { LoggerService } from "@bfx/logger";
import { ConstraintValue } from "../../../../shared/cubiq-services/model/core/ConstraintValue";
const logger = LoggerService.for(__dirname);

export class OEDataModel {
  /************************************************************/
  /* MODEL IDs */
  public refId: string = null;
  public orderId: string = null;
  public orderRef: string = null;
  public strategyRef: string = null;
  /************************************************************/
  /* ORDER REQUEST MODEL */
  public requestModel: CubiqTS.LinkedOrderRequestImpl | CubiqTS.LinkedOrderModificationRequestImpl =
    null;
  public requestModifyInfoModel: CubiqTS.LinkedOrderInfoModificationRequestImpl = null;
  public bmarkRequestModel: CubiqTS.FixingOrderRequestImpl = null;

  /************************************************************/
  /* INTERFACE DATA MODEL */
  /**  client details */
  public client: CubiqTS.ClientImpl = null; // ClientImpl
  public _account: CubiqTS.AccountImpl = null; // AccountImpl

  public get account() {
    return this._account;
  }

  public set account(account: CubiqTS.AccountImpl) {
    if (account == null) {
      logger.out.warn(`${this.constructor.name} set account to null `);
    }

    this._account = account;
  }

  public clientTrader: ICAutocompleteGenericModel | CubiqTS.UserImpl = null; // BfxAutocompleteGenericModel
  public contacts: Array<CubiqTS.ClientContactImpl> = null; //
  public clActiveTab: string = "";
  public accActiveTab: string = "";
  // financials
  public orderType: ICAutocompleteGenericModel = null; // number
  public algoType: ICAutocompleteGenericModel = null; // string
  public ccyPair: string = null; // string
  public ccyPairModel: CubiqTS.CurrencyPairImpl = null; // CurrencyPairImp
  public clientIntent: CubiqTS.OrderIntent = null;
  public fixedCcy: ICFixedCcyModel = null;
  public orderAmount: CubiqTS.BigNumber = null; // BigNumber
  public requestPrice: CubiqTS.BigNumber = null;
  public trailingPips: CubiqTS.BigNumber = null;
  public trailingTrigger: CubiqTS.BigNumber = null;
  public trailingTriggerProvided: boolean = false;
  public limitPrice: ICLimitPriceModel = null; // in algos
  public valueDate: ICTenorValueDateModel = null; // string
  public goodFrom: ICGoodFromUntilModel = null; // CubiqTS.moment.Moment = null;
  public goodUntil: ICGoodFromUntilModel = null; // CubiqTS.moment.Moment = null;
  public suspend: ICSuspendModel = null;
  public markup: ICMarkupModel = null;
  public trigger: ICAutocompleteGenericModel = null;
  public fixingSource: ICFixingSourceModel = null;

  /** FwdNettingId set when editing an order from Scratchpad */
  public fwdNettingId: string;

  public fixingDate: Date = null;
  public fixingValidationError: boolean = false;

  public fixingCode: string = null;
  public fixingTime: string = null;
  public fixingTimezone: string = null;

  // more section
  public clientComment: ICCommentsModel = null; //string
  public privateComments: string = null;
  public holdSTP: boolean = false;
  public autoForwardPrice: boolean;
  public privateOrder: boolean = false;
  public executionMode: CubiqTS.ExecutionType = null;
  public traderGroup: CubiqTS.TradingGroupImpl = null;
  public allowPartialFills: ConstraintValue = ConstraintValue.None;
  /**  strategy */
  public strategy: OEStrategyModel = new OEStrategyModel();
  /**  algo-strategy */
  public algoParameters: Array<ATDLParameterDataModel> = null; //= new Array<ATDLParameterDataModel>();
  // privatization locking:
  // UNLOCKs AND with strategy profile
  // LOCKs OR with strategy profile
  public privatizationLockProfile: OEPrivatizationLockingProfileModel =
    new OEPrivatizationLockingProfileModel();
  /************************************************************/
  /* ADDITIONAL DATA */
  public traderGroupCodeId: string = null; // number as a string  - used for sending abort on log out
  //
  public clientBranch: string = null;

  /** Pass Through Parameters only relevant for modify, attach them on OrderRequestData as you got them, no processing whatsoever at the moment*/
  public passthroughParameters: Array<CubiqTS.StrategyParameterImpl> = null;

  /**  STP Aggregation flag, same behaviour as pass through and is propagated from each strategy leg */
  public stpAggregationFlag: ConstraintValue = ConstraintValue.None;

  /**  OBO Client */
  public oboClient: string = null;

  /**  OBO Account */
  public oboAccount: string = null;

  /**  OBO User. Reserved */
  public oboUser: string = null;

  // Gamma specific fields
  public isGamma: boolean = false;

  public delta: string = "0.0";

  public rungsAbove: number = 0;

  public rungsBelow: number = 0;

  // multi-allocation
  public allocations: CubiqTS.AllocationRequestData[] = [];
  public farAllocations: CubiqTS.AllocationRequestData[] = [];
  public product: CubiqTS.Product = null;
  public swapFixedAmount: CubiqTS.BigNumber = null;
  public swapTenor: CubiqTS.Tenor = null;
  public swapValueDate: CubiqTS.StandardDateImpl = null;
  public bulking: boolean = false;
  public bulked: boolean = false;

  /************************************************************/
  /* CONTROL FG */
  /**  Indicates what type of order entry is this model: SPOT, ALGO, BENCHMARK */
  public entryType: OENEntryType = OENEntryType.SPOT;
  /**  Indicates whether the order entry is operating in NEW, DUPLICATE, MODIFY, MODIFY_INFO, AMEND_MARKUP mode */
  private _operationMode: OENOperationMode = OENOperationMode.NEW;
  public get operationMode(): OENOperationMode {
    return this._operationMode;
  }
  public set operationMode(value: OENOperationMode) {
    logger.out.info(`oed-m - Setting operationMode to ${OENOperationMode[value]}`);
    this._operationMode = value;
  }
  /**  This enum indicates the current form state of the order entry display */
  private _displayState: OENDisplayState = OENDisplayState.SHOW_LOADING;
  public get displayState(): OENDisplayState {
    return this._displayState;
  }
  public set displayState(value: OENDisplayState) {
    logger.out.info(
      `oed-m - Setting displayState to ${OENDisplayState[value]} for order ${this.orderId}`,
    );
    this._displayState = value;
  }
  /**  Indicates whether the order has been: partially filled, fully filled or not at all. (Used in modify) */
  public fillState: CubiqTS.FillState = CubiqTS.FillState.NONE;
  /**  Server state of the order */
  public srvOrderState: CubiqTS.OrderState = null;
  /**  accept after tolerance failure */
  public toleranceAccept: boolean = null;
  /**  accept after tolerance amount failure */
  public toleranceAmountAccept: boolean = null;
  /**  Indicates whether the service actively listens for order life-cycle events associated with the order entry worker events. */
  public subscriptionLifeCycleActive: boolean = false; // do not reset locally
  /**  Indicates whether the service actively listens for intervention events associated with the order entry worker events. */
  public subscriptionInterventionActive: boolean = false; // do not reset locally
  /**  Indicates whether the service actively listens for scratchpad events associated with the order entry worker events. */
  public subscriptionScratchpadActive: boolean = false; // do not reset locally

  /**  Indicates whether the order is locked for modification */
  public isLockedForModification: boolean = false;
  /**  User id of locking user - useful when monitoring orders in a ticket */
  public lockUserId: string = null;
  /**  Privatization General Field ->  Modify, ModifyInfo, Amend Markup */
  public isModificationPrivatized: boolean = false;
  //
  public isClientTraderPrivatized: boolean = false;
  //
  public isContactsPrivatized: boolean = false;
  //
  public isModificationClientCommentsPrivatized: boolean = false;
  //
  public isModificationPrivateCommentsPrivatized: boolean = false;
  /**  Indicates whether the order had errors in the last external validation */
  public hasValidationErrors: boolean = false;
  /**  Indicates whether the order is active or completed (filled, expired), affects validation */
  public get isInTerminalState(): boolean {
    // false => active; true => complete
    // This can be reset by reject of modification so synth based on srvOrderState
    return (
      CubiqTS.OrderState.EXPIRED === this.srvOrderState ||
      CubiqTS.OrderState.CANCELLED === this.srvOrderState ||
      CubiqTS.OrderState.DELETED === this.srvOrderState ||
      CubiqTS.OrderState.FILLED === this.srvOrderState ||
      CubiqTS.OrderState.REJECTED === this.srvOrderState
    );
  }
  /**  indicates whether the model is loaded in the order entry form */
  public isActivelyDisplayed: boolean = false;
  /**  holds orderId of the strategy leg upon modify was issued */
  public modifyIssuedOrderId: string = null;
  /**  indicates whether the model instance has been created from scratch (new) or cloned from another instance. */
  public isClone: boolean = false;
  //
  public isInInitialization: boolean = false;
  //
  public eventOrderStateUpdateQueue: Array<OEUpdateEventModel> = null;
  //
  public isClientRestricted: boolean = false;
  //
  public isAccountRestricted: boolean = false;

  /** Model storing custom values */
  public customValues: Array<CubiqTS.CustomValue> = null;

  public gammaModel: OEPanelGammaViewModel;

  /** Has any leg in the strategy been filled? Used for disabling certain Custom Fields with disableOnPartFillFg = true*/
  public hasStrategyFill: boolean = false;

  public forceFixingTimeUpdate: boolean = false;

  // Added to fix OMS-31130
  /* This is needed in order to submit the same markup values to the server as were received on load
   * when the user does not have markup ROLE_ORDER_ENTRY_MARKUP_LIMIT/ALGO/BENCHMARK. The problem is that since the UI does
   * not work with MarkUpType.Decimals it is converted to pips on the UI side and consequently the type is
   * send as pips, which constitutes a change which is not permitted for the user without that role.
   * /////
   * Latest workaround uses the same markup (value, type) received from the server on load,
   * when submitting the order if the user doesn't have the permission to edit markup, regardless of the model or displayed value
   * Thus bypassing the forward markup conversion, or errors in the display (OMS-32778).
   */
  public originalMarkup: {
    value: CubiqTS.BigNumber;
    type: CubiqTS.MarkUpType;
    conversionToPipsApplied: boolean;
    submitOriginalMarkup: boolean; // should be set to true when user does not have permission to edit markup
  } = {
    value: undefined,
    type: undefined,
    conversionToPipsApplied: false,
    submitOriginalMarkup: false,
  };

  public negativeMarkupAccept: boolean = false;

  /** Flag defining where zero amount for BM has been accepted? */
  public bmZeroAmountAccept: boolean = false;

  /** vwapAllInRt reference value used by ExecutionMonitor for inactive strategy legs */
  public vwapAllInRt: CubiqTS.BigNumber;
  /** executedQty reference value used by ExecutionMonitor for inactive strategy legs */
  public executedQty: CubiqTS.BigNumber;

  /************************************************************/
  /* METHODS */
  public resetDataModel(): void {
    // reset request model
    this.requestModel = null;
    this.requestModifyInfoModel = null;
    // Client data
    this.client = null;
    this.account = null;
    this.clientTrader = null;
    this.contacts = null;
    // Financials data
    this.orderType = null;
    this.algoType = null;
    this.ccyPair = null;
    this.ccyPairModel = null;
    this.clientIntent = null;
    this.fixedCcy = null;
    this.orderAmount = null;
    this.requestPrice = null;
    this.limitPrice = null;
    this.valueDate = null;
    this.markup = null;
    this.goodFrom = null;
    this.goodUntil = null;
    this.suspend = null;
    this.traderGroup = null;
    this.trigger = null;
    this.fixingSource = null;
    this.fwdNettingId = null;
    this.fixingDate = null;
    this.fixingValidationError = false;
    // More data
    this.clientComment = null;
    this.privateComments = null;
    this.privateOrder = false;
    this.executionMode = null;
    this.holdSTP = false;
    this.autoForwardPrice = false;
    this.allowPartialFills = null;
    this.stpAggregationFlag = null;
    // strategies
    this.strategy = new OEStrategyModel(this.strategy);
    // algo-strategy
    this.algoParameters = null; // new Array<ATDLParameterDataModel>();
    this.clientBranch = null;
    // additional
    this.traderGroupCodeId = null;
    // flags
    // this.entryType = OENEntryType.SPOT;
    // this.operationMode = OENOperationMode.NEW;
    // this.displayState = OENDisplayState.SHOW_ORDER_ENTRY;
    this.fillState = CubiqTS.FillState.NONE;
    this.srvOrderState = null;
    this.toleranceAccept = null;
    this.isLockedForModification = false;
    this.lockUserId = null;
    this.isModificationPrivatized = false;
    this.isClientTraderPrivatized = false;
    this.isContactsPrivatized = false;
    this.hasValidationErrors = false;
    this.isActivelyDisplayed = false;
    this.modifyIssuedOrderId = null;
    this.isClone = false;
    this.isInInitialization = false;
    this.eventOrderStateUpdateQueue = null;
    this.oboAccount = null;
    this.oboAccount = null;
    this.delta = null;
    this.rungsAbove = null;
    this.rungsBelow = null;
    this.customValues = null;
    this.hasStrategyFill = false;
    this.gammaModel = new OEPanelGammaViewModel();
    this.vwapAllInRt = null;
    this.executedQty = null;
    this.negativeMarkupAccept = false;
    this.bmZeroAmountAccept = false;
  }

  public clone(keepIds: boolean = false): OEDataModel {
    let newModel: OEDataModel = new OEDataModel();
    if (keepIds) {
      newModel.refId = this.refId;
      newModel.orderId = this.orderId;
      newModel.orderRef = this.orderRef;
    }
    // client
    newModel.client = this.client;
    newModel.account = this.account;
    newModel.clientTrader = this.clientTrader;
    newModel.contacts = this.contacts;
    // financials
    newModel.orderType = this.orderType;
    newModel.algoType = this.algoType;
    newModel.ccyPair = this.ccyPair;
    newModel.ccyPairModel = this.ccyPairModel;
    newModel.clientIntent = this.clientIntent;
    newModel.fixedCcy = this.fixedCcy;
    if (this.orderAmount) {
      newModel.orderAmount = new CubiqTS.BigNumber(this.orderAmount);
    }
    newModel.allowPartialFills = this.allowPartialFills;
    newModel.stpAggregationFlag = this.stpAggregationFlag;

    newModel.requestPrice = this.requestPrice;
    newModel.trailingPips = this.trailingPips;
    newModel.trailingTrigger = this.trailingTrigger;
    newModel.trailingTriggerProvided = this.trailingTriggerProvided;
    newModel.limitPrice =
      this.limitPrice != null ? Object.assign(new ICLimitPriceModel(), this.limitPrice) : null;
    newModel.valueDate =
      this.valueDate != null
        ? new ICTenorValueDateModel(this.valueDate.tenor, this.valueDate.valueDate)
        : null;

    if (this.markup instanceof ICMarkupModel) {
      newModel.markup = new ICMarkupModel();
      newModel.markup.value =
        this.markup.checkbox.value && this.markup.value != null
          ? new CubiqTS.BigNumber(this.markup.value)
          : null;
      newModel.markup.textValue =
        this.markup.checkbox.value && this.markup.textValue != "" ? this.markup.textValue : "";
      newModel.markup.typeValue =
        this.markup.checkbox.value && this.markup.typeValue != null
          ? Object.assign(new ICAutocompleteGenericModel(), this.markup.typeValue)
          : null;
      newModel.markup.checkbox.enabled = this.markup.checkbox.enabled;
      newModel.markup.checkbox.value = this.markup.checkbox.value
        ? this.markup.checkbox.value
        : false;
    } else {
      newModel.markup = null;
    }

    newModel.trigger = this.trigger;
    newModel.fixingSource = this.fixingSource;
    newModel.fwdNettingId = this.fwdNettingId;
    newModel.fixingCode = this.fixingCode;
    newModel.fixingDate = this.fixingDate;
    newModel.fixingTime = this.fixingTime;
    newModel.fixingTimezone = this.fixingTimezone;
    newModel.traderGroupCodeId = this.traderGroupCodeId;
    // more section
    newModel.clientComment = Object.assign(new ICCommentsModel(), this.clientComment);
    newModel.privateComments = this.privateComments;
    if (this.goodFrom) {
      // newModel.goodFrom=CubiqTS.moment(newModel.goodFrom);
      newModel.goodFrom = Object.assign(new ICGoodFromUntilModel(), this.goodFrom);
    }
    if (this.goodUntil) {
      // newModel.goodUntil=CubiqTS.moment(newModel.goodUntil);
      newModel.goodUntil = Object.assign(new ICGoodFromUntilModel(), this.goodUntil);
    }
    if (this.suspend) {
      newModel.suspend = new ICSuspendModel();
      if (this.suspend.suspendFrom) {
        newModel.suspend.suspendFrom = Object.assign(
          new ICTimeControlModel(),
          this.suspend.suspendFrom,
        );
      }
      if (this.suspend.suspendTo) {
        newModel.suspend.suspendTo = Object.assign(
          new ICTimeControlModel(),
          this.suspend.suspendTo,
        );
      }
      newModel.suspend.activated = this.suspend.activated;
    }
    newModel.holdSTP = this.holdSTP;
    newModel.autoForwardPrice = this.autoForwardPrice;
    newModel.privateOrder = this.privateOrder;
    newModel.executionMode = this.executionMode;
    newModel.traderGroup = this.traderGroup;
    // strategy
    newModel.strategy = new OEStrategyModel(this.strategy);
    // algo-strategy
    newModel.algoParameters = this.cloneParameters(this.algoParameters);

    if (this.customValues) {
      // MAke a shallow-clone duplicating  all custom values
      newModel.customValues = this.customValues.map((customValue) =>
        Object.assign(new CubiqTS.CustomValueImpl(), customValue),
      );
    }
    /************************************************************/
    /* ADDITIONAL DATA */
    newModel.entryType = this.entryType;
    newModel.operationMode = this.operationMode;
    newModel.displayState = this.displayState;
    newModel.isLockedForModification = this.isLockedForModification;
    newModel.lockUserId = this.lockUserId;
    /* newModel.isModificationPrivatized = this.isModificationPrivatized;
		newModel.isClientTraderPrivatized = this.isClientTraderPrivatized;
		newModel.isContactsPrivatized = this.isContactsPrivatized; */
    //newModel.isActivelyDisplayed = this.isActivelyDisplayed;
    newModel.modifyIssuedOrderId = this.modifyIssuedOrderId;
    // Override
    newModel.fillState = CubiqTS.FillState.NONE;
    newModel.isClone = true;
    //
    newModel.isInInitialization = false;
    newModel.eventOrderStateUpdateQueue = null;

    newModel.oboClient = this.oboClient;
    newModel.oboAccount = this.oboAccount;

    newModel.delta = this.delta;
    newModel.rungsAbove = this.rungsAbove;
    newModel.rungsBelow = this.rungsBelow;

    newModel.hasStrategyFill = this.hasStrategyFill;

    if (this.gammaModel != null) {
      newModel.gammaModel = this.gammaModel.cloneViewModel();
    }

    if (this.bulked || this.bulking) {
      // will be unbulked by operation
      const cloneAllocations = (a) => {
        return Object.assign(new CubiqTS.AllocationRequestDataImpl(), a);
      };

      newModel.allocations = Array.isArray(this.allocations)
        ? this.allocations.map(cloneAllocations)
        : [];
      newModel.farAllocations = Array.isArray(this.farAllocations)
        ? this.farAllocations.map(cloneAllocations)
        : [];
      newModel.bulked = this.bulked;
      newModel.bulking = this.bulking;
    }

    newModel.vwapAllInRt = this.vwapAllInRt;
    newModel.executedQty = this.executedQty;

    return newModel;
  }

  public cloneParameters(obj: any): any {
    let copy;
    // Handle the 3 simple types, and null or undefined
    if (obj == null || typeof obj != "object") return obj;
    // Handle Date
    if (obj instanceof Date) {
      copy = new Date();
      copy.setTime(obj.getTime());
      return copy;
    }
    // Handle Array
    if (obj instanceof Array) {
      copy = [];
      for (let i = 0, len = obj.length; i < len; i++) {
        copy[i] = this.cloneParameters(obj[i]);
      }
      return copy;
    }
    // Handle Object
    if (obj instanceof Object) {
      if (obj instanceof ATDLParameterDataModel) {
        copy = new ATDLParameterDataModel();
        // Handle ATDLParameterEnumModel
      } else if (obj instanceof ATDLEnumModel) {
        copy = new ATDLEnumModel();
        // Handle ICD value models
      } else if (obj instanceof ICDAuxCheckboxModel) {
        copy = new ICDAuxCheckboxModel();
      } else if (obj instanceof ICDAutocompleteModel) {
        copy = new ICDAutocompleteModel();
      } else if (obj instanceof ICDSpinnerModel) {
        copy = new ICDSpinnerModel();
      } else if (obj instanceof ICDTextFieldModel) {
        copy = new ICDTextFieldModel();
      } else if (obj instanceof ICDClockModel) {
        copy = new ICDClockModel();
      } else if (obj instanceof ICDRadioInputModel) {
        copy = new ICDRadioInputModel();
      } else if (obj instanceof ICDCheckboxModel) {
        copy = new ICDCheckboxModel();
      } else if (obj instanceof CubiqTS.BigNumber) {
        return new CubiqTS.BigNumber(obj);
      } else {
        copy = {};
      }

      for (let attr in obj) {
        if (obj.hasOwnProperty(attr)) {
          copy[attr] = this.cloneParameters(obj[attr]);
        }
      }
      return copy;
    }

    throw new Error("Unable to copy obj! Its type isn't supported.");
  }
}
